import {
  Close as CloseIcon,
  Chat as ChatIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  Paper,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Cookie from 'js-cookie';
import React, { useState, useEffect } from 'react';

import { getCookie } from 'common/utils/cookies';

import {
  postAi,
  postAiTimesheet,
  postAiReport,
} from '../../../../../common/api';
import { useTranslation } from '../../../../utils/getLocalString';
import AiIcon from '../../icons/AiIcon';
import AiIconSearch from '../../icons/AiIconSearch';
import AiIconTools from '../../icons/AiIconTools';
import WarningAiIcon from '../../icons/WarningAiIcon';

// Original class: chatContainer
const StyledChatContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: '44px !important',
  right: '44px !important',
  zIndex: theme.zIndex.modal,
}));

// Original class: chatButton
const StyledChatButton = styled(Button)(({ theme }) => ({
  borderRadius: 26,
  transition: 'all 0.3s ease',
  boxShadow: theme.shadows[4],

  '& .MuiButton-startIcon': {
    display: 'flex !important',
    margin: '0 !important',
    alignItems: 'center',
    justifyContent: 'center',
  },

  '&:hover': {
    width: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: 28,
    boxShadow: theme.shadows[6],
  },
}));

// Original class: chatWindow
const StyledChatWindow = styled(Paper)(({ theme }) => ({
  width: 500,
  height: 500,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px !important',
  padding: '20px !important',
  overflow: 'hidden',
  position: 'relative',
  boxShadow: theme.shadows[8],
  backgroundColor: '#E4E4EF',

  [theme.breakpoints.down('sm')]: {
    width: '90vw',
    height: '80vh',
  },
}));

// Original class: closeButton
const StyledCloseButton = styled(Box)(({ theme }) => ({
  position: 'absolute !important',
  top: theme.spacing(1),
  right: theme.spacing(1),
  width: '20px !important',
  height: '20px !important',
  padding: 0,
  zIndex: 1,
  color: theme.palette.text.secondary,

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },

  '& .MuiSvgIcon-root': {
    width: '20px !important',
    height: '20px !important',
    fontSize: '1.25rem !important',
  },
}));
// Original class: chatHeader
const StyledChatHeader = styled(Box)(({ theme }) => ({
  fontSize: '24px',
  padding: theme.spacing(2),
  color: '#082253',
}));
// Original class: messagesContainer
const StyledMessagesContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.grey[50],
}));
// Original class: messageBubble
const StyledMessageBubble = styled(Paper, {
  shouldForwardProp: prop => prop !== 'isUser',
})(({ theme, isUser }) => ({
  maxWidth: '80%',
  backgroundColor: isUser ? '#6270D0 !important' : '#F1F2F8 !important',
  color: isUser ? '#FFFFFF !important' : theme.palette.text.primary,
  padding: '10px',
  borderRadius: '16px 16px 16px 0px !important',
  alignSelf: 'flex-start',
  border: isUser ? 'none' : `1px solid ${theme.palette.grey[200]}`,
  marginBottom: theme.spacing(1),
  wordBreak: 'break-word',
  boxShadow: theme.shadows[1],
}));
// Original class: inputContainer
const StyledInputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));
// Original class: form
const StyledForm = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));
// Original class: textField
const StyledTextField = styled(Box)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 20,
    backgroundColor: theme.palette.grey[50],
  },
}));
// Original class: loadingIndicator
const StyledLoadingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(1),
}));

const ChatBot = () => {
  const getLocalString = useTranslation();
  const messagesEndRef = React.useRef(null);
  const accessToken = getCookie('access_token');
  const chatTexts = {
    initialMessage: getLocalString('ChatBot', 'messages', 'initialMessage'),
    connectionError: getLocalString('ChatBot', 'messages', 'connectionError'),
    connectionTimeoutError: getLocalString(
      'ChatBot',
      'messages',
      'connectionTimeoutError',
    ),
    inputPlaceholder: getLocalString('ChatBot', 'messages', 'inputPlaceholder'),
    chatTitle: getLocalString('ChatBot', 'messages', 'chatTitle'),
    chatTitleSecond: getLocalString('ChatBot', 'messages', 'chatTitleSecond'),
    aiButton: getLocalString('ChatBot', 'messages', 'aiButton'),
    responsePrefix: getLocalString('ChatBot', 'messages', 'responsePrefix'),
    serverErrorMessage: getLocalString(
      'ChatBot',
      'messages',
      'serverErrorMessage',
    ),
  };
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: chatTexts.initialMessage, isUser: false },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSendWarning = async () => {
    try {
      setIsLoading(true);
      const clearMessages = messages.filter(message => !message.errorMessage);
      const response = await postAiReport({
        request: clearMessages,
        //isTimesheet: aiType === 'tools',
      });
      const botMessage = {
        text: getLocalString('ChatBot', 'messages', 'errorReportSend'),
        isUser: false,
        errorMessage: true,
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const sessionCookie = Cookie.get('_session_id');
    if (sessionCookie) {
      setSessionId(sessionCookie);
    } else {
      setSessionId(`temp_${Math.random().toString(36).substring(2, 9)}`);
    }
  }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);
  const parseAIResponse = response => {
    try {
      const parsedResponse = JSON.parse(response);
      if (parsedResponse.endpoint) {
        return {
          type: 'link',
          endpoint: parsedResponse.endpoint,
          params: parsedResponse.params || {},
          originalText: response,
        };
      }
      return {
        type: 'text',
        text: response,
      };
    } catch (e) {
      return {
        type: 'text',
        text: response,
      };
    }
  };

  const buildDynamicLink = (endpoint, params) => {
    let newParams = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return `${window.location.origin}/${endpoint}${newParams ? `?${newParams}` : ''}`;
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) {
      return;
    }

    const userMessage = { text: inputValue, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const requestData = {
        session_id: sessionId,
        input: inputValue,
        token: accessToken,
      };

      //const response = await (aiType === 'tools' ? postAiTimesheet(requestData) : postAi(requestData));
      const response = await postAi(requestData);

      if (response.status >= 500) {
        const errorMessage = {
          text: chatTexts.serverErrorMessage,
          isUser: false,
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }
      console.log('aiResponse:');
      console.log(response.data?.response);
      const aiResponse = parseAIResponse(response.data?.response);
      console.log('aiResponse parsed:');
      console.log(aiResponse);
      if (aiResponse.type === 'link') {
        const linkUrl = buildDynamicLink(
          aiResponse.endpoint,
          aiResponse.params,
        );
        const botMessage = {
          text: (
            <Box>
              <Box>{getLocalString('ChatBot', 'messages', 'request')}</Box>
              <Link
                href={linkUrl}
                target={'_blank'}
                underline='hover'
                sx={{
                  '&&': {
                    color: '#3448FF',
                    fontWeight: 'bold',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  },
                }}
              >
                {linkUrl}
              </Link>
            </Box>
          ),
          isUser: false,
        };
        setMessages(prev => [...prev, botMessage]);
        window.open(linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        const botMessage = {
          text: aiResponse.text,
          isUser: false,
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error(getLocalString('ChatBot', 'messages', 'error'), error);
      const errorMessage = {
        text:
          error.response?.status >= 500
            ? chatTexts.serverErrorMessage
            : error.message || chatTexts.connectionError,
        isUser: false,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledChatContainer
      sx={{
        '&&': {
          bottom: '44px !important',
          right: '44px !important',
        },
      }}
    >
      {!open && (
        <StyledChatButton
          variant='contained'
          color='primary'
          //startIcon={aiType === 'tools' ? <AiIconTools /> : <AiIconSearch />}
          startIcon={<AiIcon />}
          sx={{
            '&& .MuiButton-startIcon': {
              display: 'flex !important',
              margin: '0 !important',
            },
            '&&': {
              width: '52px',
              height: '52px',
              minWidth: '52px',
              paddingLeft: 0,
              paddingRight: 0,
              borderRadius: 26,
              transition: 'opacity 0.3s ease, width 0.3s ease',
              '& .MuiSvgIcon-root': {
                width: '18.67px !important',
                height: '18.67px !important',
                fontSize: '18.67px !important',
              },

              /*'& .MuiSvgIcon-root': {
                width: '32px !important',
                height: '32px !important',
                fontSize: '32px !important',
              }, */
              '& span:not(.MuiButton-startIcon)': {
                width: 0,
                opacity: 0,
                transition: 'opacity 0.3s ease, width 0.3s ease',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              },
              '&:hover': {
                width: 'auto',
                transition: 'opacity 0.3s ease, width 0.3s ease',
                paddingLeft: 2,
                paddingRight: 2,
                '& span:not(.MuiButton-startIcon)': {
                  width: 'auto',
                  color: '#FFFFFF',
                  fontFamily: 'Nunito',
                  fontWeight: '600',
                  fontSize: '16px',

                  transition: 'opacity 0.3s ease, width 0.3s ease',
                  opacity: 1,
                  marginLeft: '10px',
                },
                '& .MuiButton-startIcon': {
                  marginRight: 1,
                },
              },
            },
          }}
          onClick={handleOpen}
        >
          <span>{chatTexts.aiButton}</span>
        </StyledChatButton>
      )}

      {open && (
        <StyledChatWindow
          sx={{
            '&&': {
              //width: '30%',
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '16px !important',
              padding: '20px !important',
              overflow: 'hidden',
              position: 'relative',
              backgroundColor: '#E4E4EF',
            },
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              '&&': {
                position: 'absolute',
                top: '19px',
                right: '15px',
                width: '40px',
                height: '40px',
                minWidth: '40px',
                padding: '0 !important',
                '& .MuiSvgIcon-root': {
                  width: '24px !important',
                  height: '24px !important',
                  fontSize: '24px !important',
                },
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Tooltip
            title={getLocalString('ChatBot', 'messages', 'errorReportIcon')}
          >
            <IconButton
              onClick={handleSendWarning}
              sx={{
                '&&': {
                  position: 'absolute',
                  top: '19px',
                  right: '55px',
                  width: '40px',
                  height: '40px',
                  minWidth: '40px',
                  padding: '0 !important',
                  '& .MuiSvgIcon-root': {
                    width: '18px !important',
                    height: '18x !important',
                    fontSize: '18px !important',
                  },
                },
              }}
            >
              <WarningAiIcon />
            </IconButton>
          </Tooltip>
          <StyledChatHeader
            sx={{
              '&&': {
                background: '#E4E4EF',
                display: 'block',
                marginBottom: '20px',
              },
            }}
          >
            <Typography
              variant='h2'
              sx={{
                '&&': {
                  display: 'flex',
                  fontSize: '24px',
                  gap: '8px',
                  color: '#082253',
                },
              }}
            >
              {chatTexts.chatTitle}
              <Box
                sx={{
                  '&&': {
                    fontSize: '20px',
                    marginTop: '2px',
                    color: '#9EA8E2',
                  },
                }}
              >
                {chatTexts.chatTitleSecond}
              </Box>
            </Typography>
          </StyledChatHeader>
          <StyledMessagesContainer
            sx={{
              '&&': {
                padding: '20px',
                marginBottom: '20px',
                borderRadius: '16px',
                border: '1px solid #E4E4EF',
              },
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                  padding: '10px',
                  mb: 1,
                }}
              >
                <StyledMessageBubble
                  isUser={message.isUser}
                  sx={{
                    ...(message.isUser
                      ? {
                          color: 'white',
                        }
                      : {
                          color: 'black',
                        }),
                    backgroundColor: message.isUser
                      ? '#6270D0 !important'
                      : 'F1F2F8  !important',
                  }}
                >
                  <Typography sx={{ whiteSpace: 'pre-line' }}>
                    {message.text}
                  </Typography>
                </StyledMessageBubble>
              </Box>
            ))}
            {isLoading && (
              <Box
                sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}
              >
                <StyledMessageBubble isUser={false}>
                  <CircularProgress size={20} />
                </StyledMessageBubble>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </StyledMessagesContainer>
          <StyledInputContainer
            sx={{
              '&&': {
                background: 'initial',
                border: 'none',
              },
            }}
          >
            <StyledForm
              component='form'
              onSubmit={handleSubmit}
              sx={{
                '&&': {
                  background: 'initial',
                },
              }}
            >
              <TextField
                sx={{
                  '&&': {
                    background: '#FFFFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E4E4EF',
                  },
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                  '& .MuiOutlinedInput-root': {
                    height: 'auto',
                  },
                  '& textarea': {
                    lineHeight: '1.5',
                    padding: '0',
                    border: 'none',
                    resize: 'none',
                  },
                }}
                fullWidth
                multiline
                minRows={1}
                maxRows={7}
                variant='outlined'
                placeholder={chatTexts.inputPlaceholder}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                disabled={isLoading}
                size='small'
              />
              <Button
                type='submit'
                variant='contained'
                color='primary'
                sx={{
                  '&&': {
                    width: '40px',
                    minWidth: '40px',
                    height: '40px',
                    background: '#DCDFF4',
                    padding: '4px',
                    marginLeft: '12px',
                    borderRadius: '20px',
                  },
                }}
                disabled={!inputValue.trim() || isLoading}
                endIcon={
                  isLoading ? <CircularProgress size={24} /> : <SendIcon />
                }
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <SendIcon
                    sx={{
                      '&&': {
                        width: '24px !important',
                        marginLeft: '5px',
                        height: '24px !important',
                        color: '#7174AC',
                      },
                    }}
                  />
                )}
              </Button>
            </StyledForm>
          </StyledInputContainer>
        </StyledChatWindow>
      )}
    </StyledChatContainer>
  );
};

export default ChatBot;
